import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getOcpiPartner, updateRemoteParty, updateRemotePartyModules } from "../../api/RemotePartiesOCPI";
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import PartyFormDetails from "../../components/ocpiParty/PartyFormDetails";
import ConfirmationModal from "../../components/ConfirmationModal";

import EntityMultiSelectForm from "../../components/EntityMultiSelectForm";
import { getAllChargingSites } from "../../api/ChargingSites"; 

const EditOcpiPartyPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const [activeTab, setActiveTab] = useState(
        query.get("tab") ? query.get("tab") : "details"
    );
    const [loading, setLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);

    const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });

    // Form state
    const [party, setParty] = useState({
        name: "",
        remotePartyName: "",
        versionNumber: "",
        RemotePartyPublicId: "",
        tokenA: "",
        tokenB: "",
        tokenC: "",
        encodedTokenA: "",
        encodedTokenB: "",
        encodedTokenC: "",
        customerName: "",
        isOcpiHandshakeCompleted: false,
        modules: [],
        remotePartyEndpoints: [],
        roles: [],
    });

    // Selected sites (ids)
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);

    const [moduleIdentifiers, setModuleIdentifiers] = useState([]);
    const [savedModuleIdentifiers, setSavedModuleIdentifiers] = useState([]);

    const sortIds = (ids) => [...ids].map(String).sort();

    const modulesSelectionDirty = () => {
        return JSON.stringify(sortIds(moduleIdentifiers)) !== JSON.stringify(sortIds(savedModuleIdentifiers));
    };

    useEffect(() => {
        const tab = query.get("tab");
        if (tab) {
            setActiveTab(tab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    useEffect(() => {
        if (activeTab === "sites" && !party.isOcpiHandshakeCompleted) {
            setActiveTab("details");
        }
    }, [activeTab, party.isOcpiHandshakeCompleted]);

    useEffect(() => {
        const q = new URLSearchParams(location.search);
        q.set("tab", activeTab);
        navigate(`${location.pathname}?${q.toString()}`, { replace: true });
    }, [activeTab, location.pathname, navigate, location.search]);

    const handleInputChange = (key, value) => {
        setHasChanges(true);
        setParty((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        handleGetOcpiParty();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGetOcpiParty = async () => {
        setLoading(true);
        try {
            const accessToken = await getAccessToken();
            const ocpiPartner = await getOcpiPartner(id, accessToken);

            setParty({
                name: ocpiPartner.name || "",
                remotePartyName: ocpiPartner.remotePartyName || "",
                versionNumber: ocpiPartner.versionNumber || "",
                remotePartyPublicId: ocpiPartner.remotePartyPublicId || "",
                tokenA: ocpiPartner.tokenA || "",
                tokenB: ocpiPartner.tokenB || "",
                tokenC: ocpiPartner.tokenC || "",
                encodedTokenA: ocpiPartner.encodedTokenA || "",
                encodedTokenB: ocpiPartner.encodedTokenB || "",
                encodedTokenC: ocpiPartner.encodedTokenC || "",
                customerName: ocpiPartner.customerName || "",
                isOcpiHandshakeCompleted: !!ocpiPartner.isOcpiHandshakeCompleted,
                modules: ocpiPartner.modules || [],
                remotePartyEndpoints: ocpiPartner.remotePartyEndpoints || [],
                roles: ocpiPartner.roles?.length
                    ? ocpiPartner.roles
                    : ocpiPartner.role
                        ? [ocpiPartner.role]
                        : [],
            });

            // Initiera selectedSiteIds fr�n partnerns sites
            const initialIds = Array.isArray(ocpiPartner.chargingSites)
                ? ocpiPartner.chargingSites
                    .map((s) => s.chargingSitePublicId)
                    .filter(Boolean)
                : [];

            setSelectedSiteIds(initialIds);

            const moduleIds = (ocpiPartner.modules || [])
                .map((m) => m.identifier)
                .filter(Boolean);
            setModuleIdentifiers(moduleIds);
            setSavedModuleIdentifiers(moduleIds);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateParty = async () => {
        const toastId = toast.loading(t("toast.updating", { entity: t("entity.ocpiParty") }));

        try {
            const accessToken = await getAccessToken();
            if (modulesSelectionDirty()) {
                const moduleResult = await updateRemotePartyModules(
                    party.remotePartyPublicId,
                    moduleIdentifiers,
                    accessToken
                );
                if (
                    moduleResult &&
                    moduleResult.credentialsPushAttempted &&
                    moduleResult.credentialsPushSucceeded === false
                ) {
                    const err = moduleResult.credentialsPushError || t("ocpiParty.credentialsPushFailed");
                    toast.error(err, { id: toastId, duration: 8000 });
                    return;
                }
            }

            const payload = {
                ...party,
                remotePartyName: party.remotePartyName,
                remotePartyPublicId: party.remotePartyPublicId,
            };

            if (party.isOcpiHandshakeCompleted) {
                payload.ChargingSitePublicIds = selectedSiteIds;
            }

            await updateRemoteParty(payload, accessToken);

            await handleGetOcpiParty();

            toast.success(t("toast.updated", { entity: t("entity.ocpiParty") }), { id: toastId });
            setHasChanges(false);
        } catch (error) {
            toast.error(error?.message || t("toast.failedUpdate", { entity: t("entity.ocpiParty") }), { id: toastId });
        }
    };

    const navigateBack = () => {
        if (hasChanges || modulesSelectionDirty()) {
            setConfirmState((s) => ({
                ...s,
                isOpen: true,
                title: t("confirm.discard"),
                confirm: () => {
                    if (navigate.length > 1) navigate(-1);
                    else navigate("/ocpi-parties");
                },
            }));
        } else {
            if (navigate.length > 1) navigate(-1);
            else navigate("/ocpi-parties");
        }
    };

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("pages.editOcpiParty")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={() => navigateBack()} className="text-black flex">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>

                            <h2 className="border-l border-mute-200 pl-4 ">{t("pages.editOcpiParty")}</h2>

                            <div className="flex gap-4 ml-auto">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => navigateBack()}
                                >
                                    {t("common.cancel")}
                                </button>

                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges && !modulesSelectionDirty()}
                                    onClick={() => handleUpdateParty()}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <div className="border-b border-mute-200 mb-4 gap-4 flex">
                                <button
                                    className={`px-4 pb-2 ${activeTab === "details"
                                            ? "border-b-2 border-black font-bold text-sm text-black"
                                            : "text-mute-400 text-sm font-bold"
                                        }`}
                                    onClick={() => setActiveTab("details")}
                                >
                                    {t("tabs.details")}
                                </button>

                                {party.isOcpiHandshakeCompleted && (
                                    <button
                                        className={`px-4 pb-2 ${activeTab === "sites"
                                                ? "border-b-2 border-black font-bold text-sm text-black"
                                                : "text-mute-400 text-sm font-bold"
                                            }`}
                                        onClick={() => setActiveTab("sites")}
                                    >
                                        {t("tabs.sites")}
                                    </button>
                                )}
                            </div>

                            {activeTab === "details" && (
                                <div>
                                    <PartyFormDetails
                                        party={party}
                                        handleInputChange={handleInputChange}
                                        moduleIdentifiers={moduleIdentifiers}
                                        onModuleIdentifiersChange={(ids) => {
                                            setModuleIdentifiers(ids);
                                        }}
                                    />
                                </div>
                            )}

                            {activeTab === "sites" && (
                                <div className="pt-2">
                                    <EntityMultiSelectForm
                                        title={t("ocpiParty.selectSites")}
                                        fetchItems={async (token) => {
                                            const list = await getAllChargingSites(token);
                                            return (list || []).map((s) => ({
                                                ...s,
                                                chargingSitePublicId: s.chargingSitePublicId,
                                                chargingSiteName:
                                                    s.chargingSiteName || s.name || s.siteName || "",
                                            }));
                                        }}
                                        labelKey="chargingSiteName"
                                        valueKey="chargingSitePublicId"
                                        selectedIds={selectedSiteIds}
                                        onChange={(ids) => {
                                            setSelectedSiteIds(ids);
                                            setHasChanges(true);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </main>

                <ConfirmationModal
                    isOpen={confirmState.isOpen}
                    title={confirmState.title}
                    onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
                    onConfirm={() => {
                        setConfirmState({ ...confirmState, isOpen: false });
                        confirmState.confirm();
                    }}
                />
            </div>
        </>
    );
};

export default EditOcpiPartyPage;
